import {
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleDollarSign,
  FileCheck2,
  Filter,
  Play,
  Plus,
  Search,
  Workflow,
} from 'lucide-react'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Modal } from '../../components/Modal'
import { useDemoStore } from '../../store'
import type { WorkflowItem, WorkflowRunItem } from '../../types'
import { DemoNotice, PageTitle, StatusBadge } from './shared'

function statusTone(status: WorkflowRunItem['status']): 'good' | 'warn' | 'danger' | 'neutral' {
  if (status === 'completed') return 'good'
  if (status === 'failed' || status === 'cancelled') return 'danger'
  if (status === 'blocked' || status === 'awaiting-approval') return 'warn'
  return 'neutral'
}

function formatDuration(milliseconds: number | null): string {
  if (milliseconds === null) return '—'
  const seconds = Math.max(0, Math.round(milliseconds / 1000))
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return minutes ? `${minutes}m ${remainder}s` : `${remainder}s`
}

export function WorkflowsPage() {
  const { workflows, runs, apiMode, launchWorkflow } = useDemoStore()
  const [selected, setSelected] = useState<WorkflowItem | null>(null)
  const [launchOpen, setLaunchOpen] = useState(false)
  const [launched, setLaunched] = useState<string | null>(null)
  const [launchError, setLaunchError] = useState<string | null>(null)
  const [launching, setLaunching] = useState(false)
  const [objective, setObjective] = useState('Prepare the next useful output using approved business context.')
  const [approvalPolicy, setApprovalPolicy] = useState('Tenant 001 governed')
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => workflows.filter(item =>
    `${item.name} ${item.outcome} ${item.module}`.toLowerCase().includes(query.toLowerCase())), [query, workflows])

  useEffect(() => {
    if (!selected) return
    const current = workflows.find(item => item.id === selected.id)
    if (current) setSelected(current)
  }, [selected, workflows])

  const openLauncher = (workflow: WorkflowItem) => {
    setSelected(workflow)
    setLaunchError(null)
    setLaunchOpen(true)
  }

  const launch = async (event: FormEvent) => {
    event.preventDefault()
    if (!selected) return
    if (selected.status !== 'ready') {
      setLaunchError('This Stage 2 workflow is registered but not enabled yet.')
      return
    }
    if (apiMode !== 'live') {
      setLaunchError('Start the local Worker on port 8787 before launching a real workflow run.')
      return
    }

    setLaunching(true)
    setLaunchError(null)
    try {
      const run = await launchWorkflow(selected.id, {
        objective: objective.trim(),
        approvalPolicy,
        owner: 'Anthon Linton',
        submittedFrom: 'workflow-catalogue',
      }, 30)
      setLaunched(`${selected.name} · ${run.id}`)
      setLaunchOpen(false)
    } catch (error) {
      setLaunchError(error instanceof Error ? error.message : 'The workflow could not be queued.')
    } finally {
      setLaunching(false)
    }
  }

  return <>
    <PageTitle
      title="Workflows"
      copy="Versioned operating routines with explicit inputs, approvals, evidence and outcomes."
      actions={<button className="button button--gold" onClick={() => { const first = workflows.find(item => item.status === 'ready'); if (first) openLauncher(first) }}><Play size={16} /> Launch workflow</button>}
    />
    <DemoNotice />
    {launched && <div className="success-banner"><CheckCircle2 size={17} /><span><strong>Live run created:</strong> {launched}</span><button className="text-button" onClick={() => setLaunched(null)}>Dismiss</button></div>}
    <div className="workflow-toolbar"><div className="search-field"><Search size={16} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search workflows…" /></div><button className="button button--outline button--small"><Filter size={15} /> Filter</button><button className="button button--outline button--small" disabled><Plus size={15} /> New template</button></div>
    <div className="workflow-catalogue">{filtered.map(item => <article key={item.id} onClick={() => setSelected(item)}><div className="workflow-card__head"><span className="workflow-icon"><Workflow size={20} /></span><StatusBadge tone={item.status === 'ready' ? 'good' : 'neutral'}>{item.status === 'ready' ? 'enabled' : 'stage 2'}</StatusBadge></div><h3>{item.name}</h3><p>{item.outcome}</p><div className="workflow-card__meta"><span><CalendarClock size={14} />{item.schedule}</span><span><FileCheck2 size={14} />{item.approvals} gates</span><span><CircleDollarSign size={14} />{item.estimatedUsage}</span></div><div className="workflow-card__footer"><span>{item.module}</span><ChevronRight size={17} /></div></article>)}</div>
    <section className="app-panel executions-panel"><div className="panel-heading"><div><h2>Recent executions</h2><p>Live D1-backed status, usage and outcome history.</p></div></div><div className="table-wrap"><table><thead><tr><th>Execution</th><th>Workflow</th><th>Status</th><th>Owner</th><th>Started</th><th>Duration</th><th>Usage</th><th>Trigger</th></tr></thead><tbody>
      {runs.length ? runs.slice(0, 30).map(run => <tr key={run.id}><td>{run.id.slice(0, 14)}…</td><td>{run.workflowName}</td><td><StatusBadge tone={statusTone(run.status)}>{run.status.replace('-', ' ')}</StatusBadge></td><td>{run.requestedBy}</td><td>{new Date(run.createdAt).toLocaleString('en-GB')}</td><td>{formatDuration(run.durationMs)}</td><td>£{run.usageCostGbp.toFixed(2)}</td><td>{run.triggerType}</td></tr>) : <tr><td colSpan={8}>No live executions yet. Start the Worker and launch the first governed task.</td></tr>}
    </tbody></table></div></section>
    <Modal open={Boolean(selected) && !launchOpen} onClose={() => setSelected(null)} title={selected?.name ?? 'Workflow'}>{selected && <div className="workflow-detail"><div className="detail-grid"><div><small>Outcome</small><strong>{selected.outcome}</strong></div><div><small>Module</small><strong>{selected.module}</strong></div><div><small>Schedule</small><strong>{selected.schedule}</strong></div><div><small>Release stage</small><strong>Stage {selected.releaseStage ?? '—'}</strong></div></div><h3>Registered workflow contract</h3><ol className="step-list"><li><span>1</span><div><strong>Collect approved context</strong><p>{selected.inputs?.length ? selected.inputs.join(', ') : 'Read only tenant-scoped approved sources.'}</p></div></li><li><span>2</span><div><strong>Prepare registered outputs</strong><p>{selected.outputs?.length ? selected.outputs.join(', ') : 'Create the versioned workflow output.'}</p></div></li><li><span>3</span><div><strong>Run policy and quality checks</strong><p>Budget and kill switches are enforced before durable execution.</p></div></li><li><span>4</span><div><strong>Capture evidence and approval</strong><p>{selected.metrics?.length ? selected.metrics.join(', ') : 'Record the run and route consequential work for review.'}</p></div></li></ol><div className="modal-actions"><button className="button button--outline" onClick={() => setSelected(null)}>Close</button><button className="button button--gold" onClick={() => openLauncher(selected)} disabled={selected.status !== 'ready'}><Play size={16} /> {selected.status === 'ready' ? 'Launch' : 'Stage 2 disabled'}</button></div></div>}</Modal>
    <Modal open={launchOpen} onClose={() => setLaunchOpen(false)} title={`Launch ${selected?.name ?? 'workflow'}`}><form className="modal-form" onSubmit={launch}><label>Objective<textarea rows={4} value={objective} onChange={event => setObjective(event.target.value)} /></label><label>Approval policy<select value={approvalPolicy} onChange={event => setApprovalPolicy(event.target.value)}><option>Tenant 001 governed</option><option>Strict — approve every stage</option><option>Draft only — no external actions</option></select></label><label>Owner<select><option>Anthon Linton</option></select></label>{launchError && <p>{launchError}</p>}<div className="usage-estimate"><CircleDollarSign size={17} /><span>External actions</span><strong>Disabled</strong></div><div className="modal-actions"><button type="button" className="button button--outline" onClick={() => setLaunchOpen(false)}>Cancel</button><button className="button button--gold" disabled={launching || apiMode !== 'live'}><Play size={16} /> {launching ? 'Adding to D1…' : 'Add to live queue'}</button></div></form></Modal>
  </>
}
